import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged, User as FirebaseUser } from 'firebase/auth';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions } from '../firebase';
import { getUserProfile, updateUserProfile } from '../services/userService';
import {
    Container,
    Card,
    CardContent,
    Typography,
    Alert,
    Button,
    Box,
    CircularProgress,
    Stepper,
    Step,
    StepLabel,
} from '@mui/material';
import { StepLocationLanguage } from './onboarding/StepLocationLanguage';
import { StepGrowPreferences } from './onboarding/StepGrowPreferences';
import { StepPriorities } from './onboarding/StepPriorities';
import SpacesManagementForm from './SpacesManagementForm';
import { Space, Task } from '../types/shared';
import { useTranslation } from 'react-i18next';
import { LoadingScreen } from './shared/Loading';
import { UserProfileData } from './UserProfileForm';

const Onboarding: React.FC = () => {
    const { t } = useTranslation();
    const [user, setUser] = useState<FirebaseUser | null>(null);
    const [profile, setProfile] = useState<Partial<UserProfileData>>({
        location: '',
        experienceLevel: 'Beginner',
        growingInterests: [],
        gardenGoals: [],
        foodPreferences: '',
        availability: '',
        language: 'en',
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [spaces, setSpaces] = useState<Space[]>([]);
    const [finishing, setFinishing] = useState(false);
    const [activeStep, setActiveStep] = useState(0);

    const navigate = useNavigate();

    const steps = [
        t('onboarding.step_location'),
        t('onboarding.step_preferences'),
        t('onboarding.step_priorities'),
        t('onboarding.step_spaces'),
    ];

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (u) => {
            if (u) {
                setUser(u);
                try {
                    const userProfile = await getUserProfile(u.uid);
                    if (userProfile) {
                        setProfile(prev => ({
                            ...prev,
                            ...userProfile,
                            // Fallback for migration
                            growingInterests: userProfile.growingInterests || (userProfile as any).gardenTypes || []
                        } as Partial<UserProfileData>));
                    }
                } catch (err) {
                    console.error("Error fetching profile", err);
                }
            } else {
                navigate('/login');
                return;
            }
            setLoading(false);
        });
        return () => unsubscribe();
    }, [navigate]);

    useEffect(() => {
        if (!user) return;
        const q = query(collection(db, 'spaces'), where('userId', '==', user.uid));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const items = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Space));
            setSpaces(items.sort((a, b) => (a.order ?? 999) - (b.order ?? 999)));
        });
        return () => unsubscribe();
    }, [user]);

    const handleFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setProfile({ ...profile, [name]: value });
        if (name === 'location' && value.trim() !== '') {
            setError('');
        }
    };

    const handleInterestsChange = (interests: string[]) => {
        setProfile({ ...profile, growingInterests: interests });
    };

    const handleGoalsChange = (goals: string[]) => {
        setProfile({ ...profile, gardenGoals: goals });
    };

    const handleNext = async () => {
        if (activeStep === 0 && (!profile.location || profile.location.trim() === '')) {
            setError(t('settings.location_required'));
            return;
        }

        setError('');
        if (activeStep < 3) {
            setSaving(true);
            try {
                if (user) {
                    await updateUserProfile(user.uid, profile);
                }
                setActiveStep((prev) => prev + 1);
            } catch (err) {
                setError('Failed to save settings. Please try again.');
            } finally {
                setSaving(false);
            }
        } else {
            handleFinish();
        }
    };

    const handleBack = () => {
        setActiveStep((prev) => prev - 1);
        setError('');
    };

    const handleFinish = async () => {
        if (!user) return;
        setFinishing(true);
        try {
            const generateTasks = httpsCallable<{ forceRegenerate: boolean }, { tasks: Task[] }>(functions, 'generateGardenTasks');
            await generateTasks({ forceRegenerate: true });
        } catch (err) {
            console.error("Error generating initial tasks:", err);
        }
        navigate('/tasks');
    };

    const renderStepContent = (step: number) => {
        switch (step) {
            case 0:
                return <StepLocationLanguage data={profile} onChange={handleFieldChange} error={error} />;
            case 1:
                return <StepGrowPreferences data={profile} onChange={handleFieldChange} onInterestsChange={handleInterestsChange} />;
            case 2:
                return <StepPriorities data={profile} onChange={handleFieldChange} onGoalsChange={handleGoalsChange} />;
            case 3:
                return user ? (
                    <SpacesManagementForm
                        mode="inline"
                        userId={user.uid}
                        spaces={spaces}
                        plants={[]}
                    />
                ) : null;
            default:
                return 'Unknown step';
        }
    };

    if (loading) return <LoadingScreen />;

    return (
        <Container maxWidth="sm" sx={{ py: 4 }}>
            <Typography variant="h4" gutterBottom align="center" sx={{ mb: 4, fontWeight: 'bold', color: 'primary.main' }}>
                {t('onboarding.welcome')}
            </Typography>

            <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
                {steps.map((label) => (
                    <Step key={label}>
                        <StepLabel>{label}</StepLabel>
                    </Step>
                ))}
            </Stepper>

            <Card elevation={0} sx={{ boxShadow: '0 4px 12px rgba(0,0,0,0.05)', borderRadius: 3 }}>
                <CardContent sx={{ p: 4 }}>
                    {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

                    {renderStepContent(activeStep)}

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                        <Button
                            disabled={activeStep === 0 || saving || finishing}
                            onClick={handleBack}
                        >
                            {t('onboarding.back')}
                        </Button>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {saving && <CircularProgress size={24} sx={{ mr: 1 }} />}
                            <Button
                                variant="contained"
                                onClick={handleNext}
                                disabled={saving || finishing}
                            >
                                {activeStep === steps.length - 1
                                    ? (finishing ? t('onboarding.setting_up') : (spaces.length > 0 ? t('onboarding.finish') : t('onboarding.skip')))
                                    : t('onboarding.next')}
                            </Button>
                        </Box>
                    </Box>
                </CardContent>
            </Card>
        </Container>
    );
};

export default Onboarding;
